import uniqBy from 'lodash.uniqby';
import { getRoot } from 'mobx-state-tree';

import { BUTTON_TYPES } from '../../../components/ShowContactByDataPoint/constants';
import { SEARCH_STATUS } from '../../../utils/constants';
import { ALL_OPTIONS_FILTER, BULK_FILTERS_KEY } from '../../../views/BulkEnrich/constants';

function getIsFilteredContact(filter, contact) {
  switch (filter) {
    case 'workEmails':
      return contact.hasWorkEmail;
    case 'directEmails':
      return contact.hasDirectEmail;
    case 'phones':
      return contact.totalPhonesCollected > 0;
    case BULK_FILTERS_KEY.wishlist:
      return contact.isWishlistContact && contact.wishlistContactDetails.length;
    case BULK_FILTERS_KEY.wishlistNoData:
      return contact.totalDataCollected === 0 && !contact.wishlistContactDetails.length;
    default:
      return contact;
  }
}

const Views = self => ({
  get totalFilters() {
    return self.seniorityFilters.length + self.departmentsFilters.length + self.emailFilters.length + self.locationFilters.length;
  },

  get contacts() {
    if (self.prospectContacts.size === 0) {
      return [];
    }

    return Array.from(self.prospectContacts.values());
  },

  get companies() {
    const rootCompanies = getRoot(self).companies;
    if (rootCompanies.size === 0) {
      return [];
    }

    return Array.from(rootCompanies.values());
  },

  get contact() {
    if (!self.currentContactId) {
      return {};
    }

    const result = self.prospectContacts.get(self.currentContactId);
    if (result) {
      return result;
    }

    return self.keyPeople.find(item => item.id === self.currentContactId);
  },

  get company() {
    if (!self.currentCompanyId) {
      return {};
    }

    return getRoot(self).companies.get(self.currentCompanyId);
  },

  get isSearching() {
    return [SEARCH_STATUS.IDLE, SEARCH_STATUS.SEARCHING, SEARCH_STATUS.RETRYING_SEARCH].includes(self.searchStatus);
  },

  get totalContactsWithData() {
    return self.contacts.reduce((total, contact) => (contact.totalDataCollected > 0) ? total + 1 : total, 0);
  },

  get isAllContactsNotEligibleToBeShown() {
    return self.selectedContacts.length > 0 ? self.selectedContacts?.every(contact => contact.hasOnlyDncPhones || contact.isBlockedForShow) : false;
  },

  get selectedContacts() {
    return self.contacts.filter(contact => contact.isChecked);
  },

  get filteredContacts() {
    if (!envConfig.FEATURES.newBulkFilters) {
      const filter = self.activeBulkFilters[0];
      const isAllContactsFilter = filter === ALL_OPTIONS_FILTER;

      if (isAllContactsFilter) {
        return self.contacts;
      }

      return self.contacts.filter(contact => getIsFilteredContact(filter, contact));
    }

    const filters = self.activeBulkFilters;

    return uniqBy(filters.map(filter => self.contacts.filter(contact => getIsFilteredContact(filter, contact))).flat(), 'id').sort((a, b) => a.ordinalId - b.ordinalId);
  },

  get selectableContacts() {
    return self.filteredContacts.filter(contact => !contact.isBulkCheckboxDisabled);
  },

  get activeBulkFilters() {
    return Array.from(self.bulkFilters.values());
  },

  get shouldSendAccountPageTypeSearchRequest() {
    const root = getRoot(self);

    // Salesforce/hubspot shouldn't send search request until the user opens the plugin
    return !!root.userSettings.isPluginOpen && root.context.isCompanyPage && root.prospecting.searchStatus !== SEARCH_STATUS.RESOLVED && root.prospecting.searchStatus !== SEARCH_STATUS.REACH_SEARCH_LIMIT;
  },

  get contactsWithSomeDncPhonesCount() {
    return self.contacts.filter(contact => contact.hasDncPhone).length;
  },

  get contactsWithOnlyDncPhonesCount() {
    return self.contacts.filter(contact => contact.hasOnlyDncPhones).length;
  },
  get contactByDataPointShowButtonType() {
    const hasEmail = self.selectedContacts.some(contact => contact.contactHasMaskedEmails);
    const hasPhone = self.selectedContacts.some(contact => contact.contactHasMaskedPhones);

    if (hasEmail && hasPhone) {
      return BUTTON_TYPES.EMAIL_AND_PHONE;
    }
    if (hasEmail) {
      return BUTTON_TYPES.EMAIL;
    }
    if (hasPhone) {
      return BUTTON_TYPES.PHONE;
    }

    return BUTTON_TYPES.NONE;
  },
  get areAllSelectedContactsRevealed() {
    return self.selectedContacts.every(contact => contact.isRevealed || contact.wishlistData);
  },
}
);

export default Views;
